/**
 * Language Context
 * Manages language/i18n state with:
 * - Database persistence for logged-in users
 * - localStorage fallback for guests
 * - Syncs with i18next
 */

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { updateLanguage as updateLanguageApi } from '@/lib/api';
import { SUPPORTED_LANGUAGES, type LanguageCode, isValidLanguage, changeLanguage } from '@/lib/i18n';

interface LanguageContextType {
  language: LanguageCode;
  setLanguage: (lang: LanguageCode) => Promise<void>;
  supportedLanguages: typeof SUPPORTED_LANGUAGES;
  isUpdating: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const STORAGE_KEY = 'resova-language';

interface LanguageProviderProps {
  children: ReactNode;
  initialLanguage?: string;
  isAuthenticated?: boolean;
}

export function LanguageProvider({ children, initialLanguage, isAuthenticated = false }: LanguageProviderProps) {
  const { i18n } = useTranslation();
  const [isUpdating, setIsUpdating] = useState(false);

  // Initialize language from: prop > localStorage > browser > 'en'
  const [language, setLanguageState] = useState<LanguageCode>(() => {
    // First check if we have a valid initial language from user data
    if (initialLanguage && isValidLanguage(initialLanguage)) {
      return initialLanguage;
    }
    // Then check localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && isValidLanguage(stored)) {
      return stored;
    }
    // Fall back to browser language or English
    const browserLang = navigator.language.split('-')[0];
    if (isValidLanguage(browserLang)) {
      return browserLang;
    }
    return 'en';
  });

  // Sync i18next with our state on mount and when language changes
  useEffect(() => {
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
    localStorage.setItem(STORAGE_KEY, language);
  }, [language, i18n]);

  // Update language when initial language prop changes (e.g., after login)
  useEffect(() => {
    if (initialLanguage && isValidLanguage(initialLanguage) && initialLanguage !== language) {
      setLanguageState(initialLanguage);
    }
  }, [initialLanguage, language]);

  // Set language with API persistence for logged-in users
  const setLanguage = useCallback(async (lang: LanguageCode) => {
    if (!isValidLanguage(lang)) {
      console.error('Invalid language code:', lang);
      return;
    }

    setIsUpdating(true);

    try {
      // Update local state and i18next immediately for responsive UX
      setLanguageState(lang);
      await changeLanguage(lang);

      // Persist to database if authenticated
      if (isAuthenticated) {
        const response = await updateLanguageApi(lang);
        if (!response.success) {
          console.error('Failed to persist language preference:', response.error);
          // Don't revert - local change is still valid
        }
      }
    } catch (error) {
      console.error('Error setting language:', error);
    } finally {
      setIsUpdating(false);
    }
  }, [isAuthenticated]);

  const value: LanguageContextType = {
    language,
    setLanguage,
    supportedLanguages: SUPPORTED_LANGUAGES,
    isUpdating,
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage(): LanguageContextType {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export { SUPPORTED_LANGUAGES, type LanguageCode };
